<template>
  <form @submit.prevent="handleSubmit">
    <div v-if="errorMessage" class="alert alert-danger py-2">
      {{ errorMessage }}
    </div>

    <!-- Nom -->
    <div class="mb-3">
      <label class="form-label fw-semibold">Nom du produit</label>
      <PrimevueInputText
        :modelValue="form.name"
        class="w-100"
        placeholder="Ex: Riz 50kg"
        required
        @update:modelValue="updateName"
      />
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-4">
        <label class="form-label fw-semibold">Type de produit</label>
        <select v-model="form.product_type" class="form-select" :disabled="isEditMode && product?.has_sales_history">
          <option value="physical">Produit physique</option>
          <option value="electronic">Produit électronique</option>
        </select>
        <small v-if="isEditMode && product?.has_sales_history" class="text-muted">Le type ne peut plus être modifié après une vente.</small>
      </div>
      <div v-if="isElectronic" class="col-md-4">
        <label class="form-label fw-semibold">Portefeuille</label>
        <select v-model.number="form.electronic_wallet_id" class="form-select" required>
          <option :value="null" disabled>Sélectionner un portefeuille</option>
          <option v-for="wallet in activeWallets" :key="wallet.id" :value="wallet.id">{{ wallet.name }} — {{ $format.currency(wallet.current_balance) }}</option>
        </select>
      </div>
      <div v-if="isElectronic" class="col-md-4">
        <label class="form-label fw-semibold">Opération électronique</label>
        <select v-model="form.electronic_operation_type" class="form-select" required>
          <option value="recharge">Recharge</option><option value="deposit">Dépôt portefeuille</option><option value="withdrawal">Retrait portefeuille</option>
        </select>
      </div>
      <small v-if="isElectronic" class="text-muted">Le montant sera saisi librement à la caisse. Ce produit ne gère aucun stock physique.</small>
    </div>

    <div class="row" data-test="product-codes-row">
      <!-- SKU -->
      <div class="col-md-6 mb-3">
        <label class="form-label fw-semibold">Code (SKU / Référence)</label>
        <PrimevueInputText
          :modelValue="form.sku"
          class="w-100"
          placeholder="Ex: RIZ-50KG-001"
          required
          @update:modelValue="updateSku"
        />
      </div>

      <!-- Code-barres -->
      <div class="col-md-6 mb-3">
        <label class="form-label fw-semibold">Code-barres</label>
        <PrimevueInputText v-model="form.barcode" class="w-100" placeholder="Ex: 3760000000012" />
        <small class="text-muted">Optionnel, mais recommandé pour le scan en caisse et la recherche rapide.</small>
      </div>
    </div>

    <div class="row">
      <!-- RAYON -->
      <div class="col-md-6 mb-3">
        <label class="form-label small fw-semibold">Rayon</label>
        <PrimevueDropdown
          v-model="form.rayon_id"
          :options="rayons"
          optionLabel="name"
          optionValue="id"
          placeholder="Sélectionner un rayon"
          class="w-100 p-inputtext-sm"
          :loading="isLoadingRayons"
        >
          <template #value>
            <span v-if="selectedRayon" class="rayon-option">
              <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(selectedRayon.color) }"></span>
              <span>{{ selectedRayon.name }}</span>
            </span>
            <span v-else class="text-muted">Sélectionner un rayon</span>
          </template>
          <template #option="{ option }">
            <span class="rayon-option">
              <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(option.color) }"></span>
              <span>{{ option.name }}</span>
            </span>
          </template>
        </PrimevueDropdown>
        <small class="text-muted">Permet de regrouper les produits dans les rayons.</small>
      </div>

      <!-- CATEGORIE -->
      <div class="col-md-6 mb-3">
        <label class="form-label small fw-semibold">Catégorie</label>
        <PrimevueDropdown
          v-model="form.category_id"
          :options="categories"
          optionLabel="label"
          optionValue="id"
          placeholder="Sélectionner une catégorie"
          class="w-100 p-inputtext-sm"
          :loading="isLoadingCategories"
        />
        <small class="text-muted">Ex : Médicaments, Parapharmacie, Digitaux…</small>
      </div>
    </div>

    <div v-if="!isElectronic" class="mb-3 border rounded p-3 bg-light" data-test="product-formula-fields">
      <div class="form-check mb-2">
        <PrimevueCheckbox v-model="form.formula_enabled" binary inputId="prodFormulaEnabled" />
        <label for="prodFormulaEnabled" class="form-check-label ms-2 fw-semibold">Activer une formule par lot</label>
      </div>
      <div v-if="form.formula_enabled" class="row g-3">
        <div class="col-md-6">
          <label class="form-label fw-semibold">Quantité de la formule</label>
          <PrimevueInputNumber v-model="form.formula_quantity" class="w-100" :min="2" />
        </div>
        <div class="col-md-6">
          <label class="form-label fw-semibold">Prix total de la formule</label>
          <PrimevueInputNumber v-model="form.formula_price" class="w-100" mode="decimal" :min="0" :maxFractionDigits="2" />
        </div>
        <small class="text-muted">Exemple : 3 unités pour 1 000 F. Les unités restantes gardent le prix normal.</small>
      </div>
    </div>

    <!-- Description -->
    <div class="mb-3">
      <label class="form-label fw-semibold">Description</label>
      <PrimevueTextarea v-model="form.description" class="w-100" autoResize rows="3"
        placeholder="Description du produit (optionnel)" />
    </div>

    <!-- Prix -->
    <div v-if="!isElectronic" class="row">
      <div class="col-md-4 mb-3">
        <label class="form-label fw-semibold">Prix d'achat par défaut</label>
        <PrimevueInputNumber v-model="form.purchase_price" class="w-100" mode="decimal" :min="0" :maxFractionDigits="2" />
        <small class="text-muted">Prix d’achat effectif du produit.</small>
      </div>
      <div class="col-md-4 mb-3">
        <label class="form-label fw-semibold">Coefficient prix de vente</label>
        <PrimevueInputNumber
          v-model="form.sale_price_coefficient"
          class="w-100"
          mode="decimal"
          :min="0"
          :maxFractionDigits="4"
        />
      </div>
      <div class="col-md-4 mb-3">
        <label class="form-label fw-semibold">Prix de vente</label>
        <PrimevueInputNumber v-model="form.sale_price" class="w-100" mode="decimal" :min="0" :maxFractionDigits="2" />
      </div>
    </div>

    <div v-if="!isElectronic" class="row">
      <div class="col-md-6 mb-3">
        <div class="form-check">
          <PrimevueCheckbox v-model="form.is_tva_applicable" binary inputId="prodTvaApplicable" />
          <label for="prodTvaApplicable" class="form-check-label ms-2">Soumis à la TVA</label>
        </div>
      </div>
      <div class="col-md-6 mb-3">
        <div class="form-check">
          <PrimevueCheckbox v-model="form.is_css_applicable" binary inputId="prodCssApplicable" />
          <label for="prodCssApplicable" class="form-check-label ms-2">Soumis à la CSS</label>
        </div>
      </div>
    </div>

    <!-- Stock -->
    <div v-if="!isElectronic" class="row">
      <div v-if="!isEditMode" class="col-md-6 mb-3">
        <label class="form-label fw-semibold">Quantité en stock</label>
        <PrimevueInputNumber v-model="form.stock_quantity" class="w-100" :min="0" />
      </div>
      <div class="col-md-6 mb-3" data-test="minimum-stock-field">
        <label class="form-label fw-semibold">Stock minimum</label>
        <PrimevueInputNumber v-model="form.min_stock_quantity" class="w-100" :min="0" />
      </div>
    </div>

    <div v-if="!isElectronic" class="mb-3 form-check" data-test="negative-stock-sales-field">
      <PrimevueCheckbox v-model="form.allow_negative_stock_sales" binary inputId="prodAllowNegativeStockSales" />
      <label for="prodAllowNegativeStockSales" class="form-check-label ms-2">
        Autoriser la vente avec stock négatif
      </label>
      <div class="form-text">
        À utiliser uniquement pour les produits vendables avant saisie des approvisionnements.
      </div>
    </div>

    <!-- Actif -->
    <div class="mb-3 form-check">
      <PrimevueCheckbox v-model="form.is_active" binary inputId="prodIsActive" />
      <label for="prodIsActive" class="form-check-label ms-2">Produit actif</label>
    </div>

    <!-- Boutons -->
    <div class="d-flex justify-content-end gap-2 mt-4">
      <button type="button" class="btn btn-outline-secondary" @click="$emit('cancel')" :disabled="isSubmitting">
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
      </button>

      <AppLoadingButton
        type="submit"
        class="btn btn-primary"
        :loading="isSubmitting"
        loading-label="Enregistrement..."
      >
        <i class="fa fa-save me-1" aria-hidden="true"></i>
        {{ isEditMode ? 'Mettre à jour' : 'Créer' }}
      </AppLoadingButton>
    </div>
  </form>
</template>

<script>
import { normalizeRayonColor } from '@/utils/rayonColor';

export default {
  name: 'ProductForm',
  props: {
    mode: { type: String, required: true },
    product: { type: Object, default: null },
  },
  data() {
    return {
      form: {
        id: null,
        name: '',
        sku: '',
        barcode: '',
        rayon_id: null,
        category_id: null,
        description: '',
        product_type: 'physical',
        electronic_wallet_id: null,
        electronic_operation_type: null,
        purchase_price: 0,
        sale_price: 0,
        sale_price_coefficient: null,
        formula_enabled: false,
        formula_quantity: null,
        formula_price: null,
        is_tva_applicable: false,
        is_css_applicable: false,
        stock_quantity: 0,
        min_stock_quantity: 0,
        allow_negative_stock_sales: false,
        is_active: true,
      },

      errorMessage: '',
      isSubmitting: false,
      isSyncingPrices: false,
      skuManuallyEdited: false,

      rayons: [],
      isLoadingRayons: false,

      categories: [],
      isLoadingCategories: false,
      wallets: [],
    };
  },
  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },
    selectedRayon() {
      return this.rayons.find((rayon) => Number(rayon.id) === Number(this.form.rayon_id)) || null;
    },
    isElectronic() { return this.form.product_type === 'electronic'; },
    activeWallets() { return this.wallets.filter((wallet) => wallet.is_active || Number(wallet.id) === Number(this.form.electronic_wallet_id)); },
  },
  watch: {
    product: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          if (this.mode === 'create') {
            this.resetForm();
          }
          return;
        }

        this.replaceForm({
          id: newVal.id,
          name: newVal.name,
          sku: newVal.sku,
          barcode: newVal.barcode || '',
          rayon_id: newVal.rayon_id || null,
          category_id: newVal.category_id || null,
          description: newVal.description || '',
          product_type: newVal.product_type || 'physical',
          electronic_wallet_id: newVal.electronic_wallet_id || null,
          electronic_operation_type: newVal.electronic_operation_type || null,
          purchase_price: newVal.purchase_price,
          sale_price: newVal.sale_price,
          sale_price_coefficient: this.coefficientFromProduct(newVal),
          formula_enabled: Boolean(newVal.formula_enabled),
          formula_quantity: newVal.formula_quantity ? Number(newVal.formula_quantity) : null,
          formula_price: newVal.formula_price !== null && newVal.formula_price !== undefined ? Number(newVal.formula_price) : null,
          is_tva_applicable: Boolean(newVal.is_tva_applicable),
          is_css_applicable: Boolean(newVal.is_css_applicable),
          stock_quantity: newVal.stock_quantity,
          min_stock_quantity: newVal.min_stock_quantity,
          allow_negative_stock_sales: Boolean(newVal.allow_negative_stock_sales),
          is_active: newVal.is_active,
        });
      },
    },
    'form.purchase_price'() {
      this.handlePurchaseOrCoefficientChange();
    },
    'form.sale_price_coefficient'() {
      this.handlePurchaseOrCoefficientChange();
    },
    'form.sale_price'() {
      this.handleSalePriceChange();
    },
  },
  mounted() {
    this.fetchRayons();
    this.fetchCategories();
    this.fetchWallets();
  },
  methods: {
    resetForm() {
      this.skuManuallyEdited = false;
      this.replaceForm({
        id: null,
        name: '',
        sku: '',
        barcode: '',
        rayon_id: null,
        category_id: null,
        description: '',
        product_type: 'physical',
        electronic_wallet_id: null,
        electronic_operation_type: null,
        purchase_price: 0,
        sale_price: 0,
        sale_price_coefficient: null,
        formula_enabled: false,
        formula_quantity: null,
        formula_price: null,
        is_tva_applicable: false,
        is_css_applicable: false,
        stock_quantity: 0,
        min_stock_quantity: 0,
        allow_negative_stock_sales: false,
        is_active: true,
      });
    },

    replaceForm(form) {
      this.isSyncingPrices = true;
      this.skuManuallyEdited = this.mode === 'edit' && String(form.sku || '').trim() !== '';
      this.form = form;
      this.$nextTick(() => {
        this.isSyncingPrices = false;
      });
    },

    updateName(value) {
      const nextName = String(value || '');
      this.form.name = nextName;

      if (!this.isEditMode && (!this.skuManuallyEdited || String(this.form.sku || '').trim() === '')) {
        this.form.sku = this.skuFromName(nextName);
      }
    },

    updateSku(value) {
      const nextSku = String(value || '');
      this.form.sku = nextSku;
      this.skuManuallyEdited = nextSku.trim() !== '';
    },

    skuFromName(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 255);
    },

    coefficientFromProduct(product) {
      if (product.sale_price_coefficient !== null && product.sale_price_coefficient !== undefined) {
        return Number(product.sale_price_coefficient);
      }

      const purchasePrice = Number(product.purchase_price || 0);
      const salePrice = Number(product.sale_price || 0);

      return purchasePrice > 0 ? this.roundCoefficient(salePrice / purchasePrice) : null;
    },

    handlePurchaseOrCoefficientChange() {
      if (this.isSyncingPrices) return;

      const coefficient = this.numericOrNull(this.form.sale_price_coefficient);
      if (coefficient === null) return;

      const purchasePrice = this.numericOrNull(this.form.purchase_price) || 0;
      this.syncPriceField('sale_price', this.roundMoney(purchasePrice * coefficient));
    },

    handleSalePriceChange() {
      if (this.isSyncingPrices) return;

      const purchasePrice = this.numericOrNull(this.form.purchase_price) || 0;
      const salePrice = this.numericOrNull(this.form.sale_price);
      const coefficient = purchasePrice > 0 && salePrice !== null
        ? this.roundCoefficient(salePrice / purchasePrice)
        : null;

      this.syncPriceField('sale_price_coefficient', coefficient);
    },

    syncPriceField(field, value) {
      if (this.form[field] === value) return;

      this.isSyncingPrices = true;
      this.form[field] = value;
      this.$nextTick(() => {
        this.isSyncingPrices = false;
      });
    },

    numericOrNull(value) {
      if (value === null || value === undefined || value === '') return null;

      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    },

    roundMoney(value) {
      return Math.round(Number(value || 0) * 100) / 100;
    },

    roundCoefficient(value) {
      return Math.round(Number(value || 0) * 10000) / 10000;
    },

    async fetchRayons() {
      this.isLoadingRayons = true;
      try {
        const res = await this.$api.get('/rayons', {
          params: { only_active: 'true' },
        });
        this.rayons = res.data.data || [];
      } catch (e) {
        console.error('Erreur chargement rayons', e);
        this.rayons = [];
      } finally {
        this.isLoadingRayons = false;
      }
    },

    rayonColor(color) {
      return normalizeRayonColor(color);
    },

    async fetchCategories() {
      this.isLoadingCategories = true;
      try {
        const res = await this.$api.get('/categories', {
          params: { only_active: 'true' },
        });

        const rows = res.data.data || [];

        this.categories = rows.map(c => ({
          id: c.id,
          label: c.code ? `${c.code} - ${c.name}` : c.name,
        }));

      } catch (e) {
        console.error('Erreur chargement catégories', e);
        this.categories = [];
      } finally {
        this.isLoadingCategories = false;
      }
    },

    async fetchWallets() {
      try { const response = await this.$api.get('/electronic-wallets'); this.wallets = response.data.data || []; }
      catch { this.wallets = []; }
    },

    async handleSubmit() {
      if (this.isSubmitting) return;

      this.errorMessage = '';
      this.isSubmitting = true;

      const payload = {
        name: this.form.name,
        sku: this.form.sku,
        barcode: this.form.barcode || null,
        rayon_id: this.form.rayon_id || null,
        category_id: this.form.category_id || null,
        description: this.form.description || null,
        product_type: this.form.product_type,
        electronic_wallet_id: this.isElectronic ? this.form.electronic_wallet_id : null,
        electronic_operation_type: this.isElectronic ? this.form.electronic_operation_type : null,
        purchase_price: this.isElectronic ? 0 : this.form.purchase_price,
        sale_price: this.isElectronic ? 0 : this.form.sale_price,
        sale_price_coefficient: this.form.sale_price_coefficient,
        formula_enabled: this.isElectronic ? false : this.form.formula_enabled,
        formula_quantity: this.form.formula_enabled ? this.form.formula_quantity : null,
        formula_price: this.form.formula_enabled ? this.form.formula_price : null,
        is_tva_applicable: this.form.is_tva_applicable,
        is_css_applicable: this.form.is_css_applicable,
        min_stock_quantity: this.isElectronic ? 0 : this.form.min_stock_quantity,
        allow_negative_stock_sales: this.isElectronic ? false : this.form.allow_negative_stock_sales,
        is_active: this.form.is_active,
      };

      if (!this.isEditMode) {
        payload.stock_quantity = this.isElectronic ? 0 : this.form.stock_quantity;
      }

      try {
        if (this.isEditMode && this.form.id) {
          await this.$api.put(`/products/${this.form.id}`, payload);
        } else {
          await this.$api.post('/products', payload);
        }

        this.$emit('saved');
      } catch (error) {
        if (error.response?.data?.errors) {
          const all = Object.values(error.response.data.errors).flat();
          this.errorMessage = all.join(' ');
        } else {
          this.errorMessage = 'Une erreur est survenue.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.rayon-option {
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.75rem;
  width: 0.75rem;
}
</style>
